import {
  Chip,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { useCurrency } from '../../../contexts/CurrencyContext';
import { ProjectsStartingItem } from '../types';
import { widgetTokens } from '../designTokens';
import { CompanyAvatar } from '../../shared/CompanyAvatar';
import { DashboardWidget } from './DashboardWidget';
import { WidgetMoreLink } from './WidgetMoreLink';

interface ProjectsStartingWidgetProps {
  items?: ProjectsStartingItem[];
  loading?: boolean;
  onNavigateToProjects?: () => void;
}

/**
 * Compact figure for the row's right-hand caption.
 *
 * **The symbol comes from the workspace, never a literal** (FI-41): this
 * hard-coded `£`, so a non-GBP workspace showed a pound sign here beside the
 * correct symbol everywhere else on the dashboard. `null` renders "—" — the
 * backend sends it for a project nobody has priced, and "£0" would assert an
 * agreed zero.
 */
function formatCompactValue(
  value: number | null,
  currency: { symbol: string; position: 'before' | 'after' },
): string {
  if (value == null) return '—';
  const abs = Math.abs(value);
  const magnitude =
    abs >= 1_000_000
      ? `${(value / 1_000_000).toFixed(1)}M`
      : abs >= 1_000
        ? `${(value / 1_000).toFixed(0)}k`
        : `${Math.round(value)}`;
  return currency.position === 'after'
    ? `${magnitude}${currency.symbol}`
    : `${currency.symbol}${magnitude}`;
}

function daysUntil(iso: string): number {
  const start = new Date(iso);
  const now = new Date();
  // Zero out time so calendar-day diff is stable across time zones.
  start.setHours(0, 0, 0, 0);
  now.setHours(0, 0, 0, 0);
  return Math.round((start.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

function urgencyLabel(days: number): {
  label: string;
  color: 'success' | 'info' | 'warning';
} {
  if (days <= 0) return { label: 'Today', color: 'warning' };
  if (days <= 7) return { label: `In ${days}d`, color: 'warning' };
  if (days <= 30) return { label: `In ${days}d`, color: 'info' };
  return { label: `In ${days}d`, color: 'success' };
}

export function ProjectsStartingWidget({
  items,
  loading,
  onNavigateToProjects,
}: ProjectsStartingWidgetProps) {
  const { currency } = useCurrency();
  const empty = !loading && (!items || items.length === 0);
  const all = items ?? [];
  const visible = all.slice(0, widgetTokens.maxRows);
  const hidden = Math.max(0, all.length - visible.length);

  return (
    <DashboardWidget
      title="Projects starting this month"
      icon={<RocketLaunchIcon fontSize="small" />}
      iconBg="warning.50"
      accentColor="warning.main"
      cta={
        onNavigateToProjects
          ? { label: 'All projects', onClick: onNavigateToProjects }
          : undefined
      }
      loading={loading}
      empty={empty}
      emptyMessage="No projects scheduled to start this month."
      data-testid="widget-projects-starting"
    >
      <List dense disablePadding>
        {visible.map((item) => {
          const days = daysUntil(item.startDate);
          const urgency = urgencyLabel(days);
          return (
            <ListItem key={item.id} disableGutters>
              <ListItemText
                slotProps={{
                  primary: { component: 'div' },
                  secondary: { component: 'div' },
                }}
                primary={
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={0.75}
                      sx={{ alignItems: 'center', minWidth: 0 }}
                    >
                      {item.company && (
                        <CompanyAvatar
                          name={item.company.name}
                          logoId={item.company.logoId}
                          icon={item.company.icon}
                          color={item.company.color}
                          size={20}
                          // The row shows no company name as text, so label the
                          // avatar for screen readers rather than hiding it.
                          ariaLabel={item.company.name}
                        />
                      )}
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500 }}
                        noWrap
                      >
                        {item.name}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {formatCompactValue(item.value, currency)}
                    </Typography>
                  </Stack>
                }
                secondary={
                  <Stack
                    direction="row"
                    spacing={0.75}
                    sx={{ alignItems: 'center', mt: 0.25 }}
                  >
                    <Chip
                      size="small"
                      color={urgency.color}
                      variant="outlined"
                      label={urgency.label}
                      sx={{ height: 18, fontSize: 10 }}
                    />
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {new Date(item.startDate).toLocaleDateString()}
                      {item.stage ? ` · ${item.stage}` : ''}
                    </Typography>
                  </Stack>
                }
              />
            </ListItem>
          );
        })}
      </List>
      <WidgetMoreLink hiddenCount={hidden} onClick={onNavigateToProjects} />
    </DashboardWidget>
  );
}
